import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useSignIn, useClerk } from '@clerk/clerk-react';
import { Mail, Check } from 'lucide-react';
import { FieldError } from 'react-hook-form';

import Checkbox from '@/components/atoms/Checkbox';
import CountdownTimer from '@/components/atoms/CountDownTimer';
import InputField from '@/components/atoms/InputField';
import PasswordField from '@/components/atoms/PasswordField';
import { Button } from '@/components/ui/button';

import { useDeviceType } from '@/hooks/useDeviceType';

import Logo from '@/assets/images/logo512.png';
import SuccessIcon from '@/assets/icons/success-check.svg';

const ForgotPasswordForm = () => {
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState<FieldError | undefined>(undefined);
  const [isTimerActive, setIsTimerActive] = useState(false);
  const [isSendingEmail, setIsSendingEmail] = useState(false);
  const [isResetCodeSent, setIsResetCodeSent] = useState(false);
  const [isCodeVerified, setIsCodeVerified] = useState(false);
  const [resetCode, setResetCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isVerifyingCode, setIsVerifyingCode] = useState(false);
  const [isResettingPassword, setIsResettingPassword] = useState(false);
  const [isPasswordResetSuccess, setIsPasswordResetSuccess] = useState(false);

  const { isMobileView } = useDeviceType();
  const { signIn, isLoaded: signInLoaded, setActive } = useSignIn();
  const { signOut } = useClerk();
  const navigate = useNavigate();

  // Password validation rules
  const passwordValidations = {
    hasMinLength: newPassword?.length >= 8,
    hasUppercase: /[A-Z]/.test(newPassword || ''),
    hasLowercase: /[a-z]/.test(newPassword || ''),
    hasNumber: /\d/.test(newPassword || ''),
    hasSpecialChar: /[!@#$%^&*]/.test(newPassword || '')
  };

  const validateEmail = (email: string): FieldError | undefined => {
    if (!email) {
      return { type: 'required', message: 'Email is required' };
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      return { type: 'pattern', message: 'Invalid email address' };
    }
    return undefined;
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setEmail(value);

    // Clear error when user starts typing
    if (emailError && value) {
      setEmailError(undefined);
    }
  };

  const handleEmailBlur = () => {
    const error = validateEmail(email);
    setEmailError(error);
  };

  const handleSendResetEmail = async () => {
    if (!signInLoaded || !signIn) {
      toast.error('Authentication not ready');
      return;
    }

    // Validate email before sending
    const error = validateEmail(email);
    if (error) {
      setEmailError(error);
      toast.error(error.message || 'Invalid email address');
      return;
    }

    setIsSendingEmail(true);
    try {
      // Create a sign-in attempt with reset password strategy
      // This automatically sends the reset code email
      await signIn.create({
        strategy: 'reset_password_email_code',
        identifier: email
      });

      setIsResetCodeSent(true);
      setIsTimerActive(true);
      toast.success('Password reset code sent to your email!');
    } catch (error: any) {
      console.error('Error sending reset email:', error);

      if (error.errors) {
        const clerkErrors = error.errors;
        clerkErrors.forEach((err: any) => {
          if (err.code === 'form_identifier_not_found') {
            toast.error('No account found with this email address');
          } else if (err.message) {
            toast.error(err.message);
          }
        });
      } else {
        toast.error(error.message || 'Failed to send reset email. Please try again.');
      }
    } finally {
      setIsSendingEmail(false);
    }
  };

  const handleResendCode = async () => {
    if (!signInLoaded || !signIn) {
      toast.error('Authentication not ready');
      return;
    }

    try {
      // Resend the reset code by creating a new sign-in attempt
      await signIn.create({
        strategy: 'reset_password_email_code',
        identifier: email
      });

      setIsTimerActive(true);
      toast.success('Password reset code resent!');
    } catch (error: any) {
      console.error('Error resending code:', error);
      toast.error(error.message || 'Failed to resend code. Please try again.');
    }
  };

  const handleVerifyCode = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!resetCode || resetCode.length !== 6) {
      toast.error('Please enter a valid 6-digit code');
      return;
    }

    // Move to password entry step
    // The actual code verification will happen when submitting the password reset
    setIsCodeVerified(true);
    toast.success('Please enter your new password');
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!signInLoaded || !signIn) {
      toast.error('Authentication not ready');
      return;
    }

    if (newPassword.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }

    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    setIsResettingPassword(true);
    try {
      // Attempt to reset the password with the code
      const resetAttempt = await signIn.attemptFirstFactor({
        strategy: 'reset_password_email_code',
        code: resetCode,
        password: newPassword
      });

      if (resetAttempt.status === 'complete') {
        // Show success state instead of redirecting
        setIsPasswordResetSuccess(true);
        toast.success('Password reset successfully!');
      } else {
        toast.error('Password reset incomplete. Please try again.');
      }
    } catch (error: any) {
      console.error('Error resetting password:', error);

      if (error.errors) {
        const clerkErrors = error.errors;
        clerkErrors.forEach((err: any) => {
          if (err.code === 'form_code_incorrect') {
            toast.error('Incorrect verification code. Please try again.');
          } else if (err.code === 'form_password_pwned') {
            toast.error('This password has been compromised in a data breach. Please choose a different password.');
          } else if (err.message) {
            toast.error(err.message);
          }
        });
      } else {
        toast.error(error.message || 'Failed to reset password. Please try again.');
      }
    } finally {
      setIsResettingPassword(false);
    }
  };

  const handleBackToLogin = async () => {
    // Sign out the automatically created session so user can log in fresh
    await signOut({ redirectUrl: '/login' });
  };

  return (
    <div className="pb-10 h-full max-w-[616px] mx-auto laptop-h:h-fit md:pr-7 md:pt-8 border md:border-0">
      {!isResetCodeSent ? (
        <form
          className={`max-w-[450px] mt-7 gap-5 mx-auto flex flex-col ${isMobileView ? 'justify-between' : 'justify-center'} py-10 px-5 md:px-0 h-full laptop-h:h-fit`}
          onSubmit={(e) => {
            e.preventDefault();
            handleSendResetEmail();
          }}
        >
          <div>
            <img className="max-w-[130px] mx-auto md:hidden" src={Logo} alt="batjet-logo" />
            <h1 className="mt-7 mb-2 text-xl text-center font-bold uppercase md:text-left md:text-[28px]">
              FORGOT PASSWORD
            </h1>
            <p className="hidden md:block text-lg text-secondary leading-[22.5px]">
              Please enter your email address to receive a code to reset your password. Enter the code along with your
              new password to regain access to your account.
            </p>
            <InputField
              className="my-4 mt-5"
              label={'Email'}
              placeholder="Enter email"
              description={!emailError ? 'A recovery code will be sent to your email' : undefined}
              name="email"
              type="email"
              value={email}
              onChange={handleEmailChange}
              onBlur={handleEmailBlur}
              error={emailError}
            />
          </div>
          <div>
            <Button
              type="submit"
              className="my-4 w-full rounded-[24px] text-base font-semibold h-[44px] md:h-[64px] md:text-[18px] uppercase"
              disabled={!email || !!emailError || isSendingEmail}
            >
              {isSendingEmail ? 'SENDING...' : 'SEND MAIL'}
            </Button>
            <p className="text-sm md:text-lg text-center">
              Remember your password?{' '}
              <button
                type="button"
                onClick={() => navigate('/login')}
                className="text-primary font-bold"
                disabled={isSendingEmail}
              >
                Sign In
              </button>
            </p>
          </div>
        </form>
      ) : !isCodeVerified ? (
        <form
          className={`max-w-[450px] mt-7 gap-5 mx-auto flex flex-col ${isMobileView ? 'justify-between' : 'justify-center'} py-10 px-5 md:px-0 h-full laptop-h:h-fit`}
          onSubmit={handleVerifyCode}
        >
          <div>
            <img className="max-w-[130px] mx-auto md:hidden" src={Logo} alt="batjet-logo" />
            <h1 className="mt-7 mb-2 text-xl text-center font-bold uppercase md:text-left md:text-[28px] flex items-center gap-2">
              {/* <Mail className="w-8 h-8 text-primary" /> */}
              <span>RESET PASSWORD</span>
            </h1>
            <p className="text-sm md:text-base text-secondary leading-[22.5px] mb-4">
              We've sent a verification code to <strong>{email}</strong>
            </p>
            <p className="text-xs md:text-sm text-secondary mb-5">
              Please check your email and enter the 6-digit code below
            </p>

            <div className="flex flex-col gap-2 mb-4">
              <label className="inline-block text-[#495057] font-medium text-base md:text-lg">Verification Code</label>
              <input
                type="text"
                placeholder="Enter 6-digit code"
                value={resetCode}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  const value = e.target.value.replace(/\D/g, ''); // Only allow digits
                  setResetCode(value);
                }}
                maxLength={6}
                className="w-full text-center text-2xl tracking-widest bg-[#F8F9FA] rounded-[20px] border border-[#DEE2E6] font-medium p-4 outline-none focus:border-[#ADB5BD]"
              />
              <p className="text-xs text-gray-500 text-center">Enter the 6-digit code from your email</p>
            </div>
          </div>
          <div>
            <Button
              type="submit"
              className="my-4 w-full rounded-[24px] text-base font-semibold h-[44px] md:h-[64px] md:text-[18px] uppercase"
              disabled={!resetCode || resetCode.length !== 6 || isVerifyingCode}
            >
              {isVerifyingCode ? 'VERIFYING...' : 'VERIFY CODE'}
            </Button>

            <div className="flex flex-col gap-3 mb-4">
              <Button
                type="button"
                onClick={() => {
                  setIsResetCodeSent(false);
                  setIsCodeVerified(false);
                  setResetCode('');
                  setNewPassword('');
                  setConfirmPassword('');
                  setIsTimerActive(false);
                }}
                variant="outline"
                className="w-full rounded-[24px] text-base font-semibold h-[44px] md:h-[64px] md:text-[18px] uppercase"
                disabled={isVerifyingCode}
              >
                BACK
              </Button>
            </div>

            <p className="text-sm md:text-lg text-center">
              {isTimerActive ? 'Please wait before able to resend code' : "Didn't receive the code?"}{' '}
              {isTimerActive ? (
                <span className="text-primary font-semibold">
                  <CountdownTimer
                    duration={60}
                    isActive={isTimerActive}
                    onActiveChange={setIsTimerActive}
                    onComplete={() => {
                      console.log('Timer completed');
                    }}
                  />
                </span>
              ) : (
                <button
                  type="button"
                  onClick={handleResendCode}
                  className="text-primary font-bold"
                  disabled={isVerifyingCode}
                >
                  Resend Code
                </button>
              )}
            </p>
          </div>
        </form>
      ) : isPasswordResetSuccess ? (
        <div
          className={`max-w-[450px] mt-7 gap-5 mx-auto flex flex-col ${isMobileView ? 'justify-between' : 'justify-center'} py-10 px-5 md:px-0 h-full laptop-h:h-fit`}
        >
          <div>
            <h1 className="mt-7 mb-2 text-xl text-center font-bold uppercase md:text-left md:text-[28px]">
              RESET PASSWORD
            </h1>
            <p className="text-sm md:text-base text-secondary leading-[22.5px] mb-5">
              Enter your new password below to finish resetting your account. Once updated, you can log in with your new
              password.
            </p>
          </div>
          <div className="flex flex-col items-center justify-center space-y-2">
            <img className="max-w-[130px] mx-auto" src={SuccessIcon} alt="success-icon" />
            <h1 className="mt-7 mb-2 text-lg text-center font-bold uppercase md:text-left md:text-[22px]">
              Password Reset Successfully
            </h1>
            <p className="text-center text-sm md:text-base text-secondary leading-[22.5px] mb-4">
              Your password has been updated! Use your new password to log in and get back to your account by clicking
              the button below.
            </p>
          </div>
          <div>
            <Button
              type="button"
              onClick={handleBackToLogin}
              className="my-4 w-full rounded-[24px] text-base font-semibold h-[44px] md:h-[64px] md:text-[18px] uppercase"
            >
              Back to Login
            </Button>
          </div>
        </div>
      ) : (
        <form
          className={`max-w-[450px] mt-7 gap-5 mx-auto flex flex-col ${isMobileView ? 'justify-between' : 'justify-center'} py-10 px-5 md:px-0 h-full laptop-h:h-fit`}
          onSubmit={handleResetPassword}
        >
          <div>
            <img className="max-w-[130px] mx-auto md:hidden" src={Logo} alt="batjet-logo" />
            <h1 className="mt-7 mb-2 text-xl text-center font-bold uppercase md:text-left md:text-[28px] flex items-center gap-2">
              {/* <Mail className="w-8 h-8 text-primary" /> */}
              <span>RESET PASSWORD</span>
            </h1>
            <p className="text-sm md:text-base text-secondary leading-[22.5px] mb-5">
              Enter your new password below to finish resetting your account. Once updated, you can log in with your new
              password.
            </p>

            <div>
              <PasswordField
                className="mb-3"
                label={'New Password'}
                placeholder="Enter new password..."
                description="Password must be at least 8 characters"
                name="newPassword"
                value={newPassword}
                onChange={(e: any) => setNewPassword(e.target.value)}
              />
              <div className="mt-3 mb-3 space-y-2">
                <div
                  className="flex items-center gap-2 text-sm transition-opacity duration-200"
                  style={{ opacity: passwordValidations.hasMinLength ? 1 : 0.6 }}
                >
                  <Check className="w-4 h-4 text-gray-500" />
                  <span>Minimum 8 characters</span>
                </div>
                <div
                  className="flex items-center gap-2 text-sm transition-opacity duration-200"
                  style={{ opacity: passwordValidations.hasUppercase ? 1 : 0.6 }}
                >
                  <Check className="w-4 h-4 text-gray-500" />
                  <span>At least one uppercase letter (A-Z)</span>
                </div>
                <div
                  className="flex items-center gap-2 text-sm transition-opacity duration-200"
                  style={{ opacity: passwordValidations.hasLowercase ? 1 : 0.6 }}
                >
                  <Check className="w-4 h-4 text-gray-500" />
                  <span>At least one lowercase letter (a-z)</span>
                </div>
                <div
                  className="flex items-center gap-2 text-sm transition-opacity duration-200"
                  style={{ opacity: passwordValidations.hasNumber ? 1 : 0.6 }}
                >
                  <Check className="w-4 h-4 text-gray-500" />
                  <span>At least one number (0-9)</span>
                </div>
                <div
                  className="flex items-center gap-2 text-sm transition-opacity duration-200"
                  style={{ opacity: passwordValidations.hasSpecialChar ? 1 : 0.6 }}
                >
                  <Check className="w-4 h-4 text-gray-500" />
                  <span>At least one special character (!@#$%^&*)</span>
                </div>
              </div>
            </div>

            <PasswordField
              className="mb-3"
              label={'Confirm Password'}
              placeholder="Confirm new password..."
              name="confirmPassword"
              value={confirmPassword}
              onChange={(e: any) => setConfirmPassword(e.target.value)}
            />
          </div>
          <div>
            <Button
              type="submit"
              className="my-4 w-full rounded-[24px] text-base font-semibold h-[44px] md:h-[64px] md:text-[18px] uppercase"
              disabled={!newPassword || !confirmPassword || isResettingPassword}
            >
              {isResettingPassword ? 'RESETTING...' : 'RESET PASSWORD'}
            </Button>

            <div className="flex flex-col gap-3 mb-4">
              <Button
                type="button"
                onClick={() => {
                  setIsCodeVerified(false);
                  setNewPassword('');
                  setConfirmPassword('');
                }}
                variant="outline"
                className="w-full rounded-[24px] text-base font-semibold h-[44px] md:h-[64px] md:text-[18px] uppercase"
                disabled={isResettingPassword}
              >
                BACK
              </Button>
            </div>
          </div>
        </form>
      )}
    </div>
  );
};

export default ForgotPasswordForm;
